import React, { useEffect, useState } from 'react';
import Select, {
    ActionMeta,
    components,
    InputActionMeta,
    OptionTypeBase
} from 'react-select';

// Material ui
import { Grid, IconButton } from '@material-ui/core';

// Components
import Typography from '@components/UI-KIT/Typography';
import Button from '@components/UI-KIT/Button';
import Divider from '@components/UI-KIT/Divider';

// Icons
import {
    ChevronBigDown, Close, More
} from '@/assets/icons';

// Styles
import '../Inputs.scss';
import VARIABLES from '@/assets/variables';

interface Props {
    value: any;
    onChange: (_value: any) => void;
    onInputChange?: (_value: any) => void;
    autoFocus?: boolean;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    name?: string;
    className?: string;
    options: SelectOption[];
    optional?: boolean;
    isClearable?: boolean;
    variant?: string;
    size?: string;
    inputValue?: string;
    noOptionsMessage?: () => string;
    md?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    lg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    sm?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    xs?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    createAction?: () => void;
    selectIcon?: React.ReactNode;
}

export interface SelectOption {
    label: string,
    value: any;
}


const SelectInput: React.FC<Props> = ({
    value,
    variant = 'primary',
    onInputChange = () => {},
    onChange,
    autoFocus = false,
    disabled = false,
    optional = false,
    label = '',
    options: optionsFromProps,
    placeholder = 'Selecciona una opción...',
    className,
    name,
    isClearable = true,
    noOptionsMessage,
    inputValue,
    md,
    lg,
    sm,
    xs,
    createAction,
    selectIcon
}) => {
    const [ { errorMsg, _inputError }, setError ] = useState({
        errorMsg: '',
        _inputError: false
    });
    const [ selectedOption, setSelectedOption ] = useState<SelectOption | null | undefined>();
    const [ optionsToShow, setOptionsToShow ] = useState<SelectOption[] | undefined>([]);

    useEffect(() => {
        setOptionsToShow(optionsFromProps);
        if (value)
            setSelectedOption(optionsFromProps.find(option => option === value) as SelectOption);
    }, [ optionsFromProps, value ]);

    const validateInput = (inputValue: unknown) => {
        if (optional) return;
        if (inputValue === null) setError({
            _inputError: true,
            errorMsg: 'Este campo es requerido'
        });
        setError({
            _inputError: false,
            errorMsg: ''
        });
    };

    const handleSelect = (option: SelectOption, { action }: ActionMeta<OptionTypeBase>) => {
        if (action !== 'select-option' || option.value === value) return;
        validateInput(option.value);
        setSelectedOption(option);
        onChange(option.value);
    };

    const WriteSelect = (value, { action }: InputActionMeta) => {
        if (action === 'input-change') {
            onInputChange(value);
        }
    };

    const MenuList = (props: any) => {
        const { children } = props;
        return (
            <components.MenuList
                {...props}
            >
                { createAction && (
                    <>
                        <Grid
                            style={{ marginLeft: '1rem' }}
                        >
                            <Button
                                level={4}
                                icon={(
                                    <More
                                        color={VARIABLES.palette.secondary_color}
                                    />
                                )}
                                label="Crear nuevo"
                                onClick={createAction}
                            />
                        </Grid>
                        <Grid
                            style={{ padding: '0 1rem' }}
                        >
                            <Divider
                                className="ui-Select-input-create-button-divider"
                            />
                        </Grid>
                    </>
                )}
                {children}
            </components.MenuList>
        );
    };

    const styles = {
        control: provided => ({
            ...provided,
            borderRadius: '.5rem',
            backgroundColor: variant === 'primary' ? VARIABLES.palette.background_color : 'inherit',
            border: variant === 'primary' ? 'none' : `1px solid ${VARIABLES.palette.content_light}`,
            borderColor: `${errorMsg ? VARIABLES.palette.error_color : VARIABLES.palette.content_light} !important`,
            boxShadow: 'none',
            fontSize: '.875rem'
        }),
        input: provided => ({
            ...provided,
            color: VARIABLES.palette.primary_dark,
            margin: 0,
            '& input': {
                width: 'auto !important'
            },
            padding: 0
        }),
        valueContainer: provided => ({
            ...provided,
            padding: '.5rem 0 .5rem 1rem',
            height: '3rem',
            marginRight: '1.5rem'
        }),
        placeholder: provided => ({
            ...provided,
            color: disabled ? VARIABLES.palette.content_light : VARIABLES.palette.content_color,
            fontSize: '.875rem',
            whiteSpace: 'nowrap'
        }),
        singleValue: provided => ({
            ...provided,
            color: disabled ? VARIABLES.palette.content_light : VARIABLES.palette.primary_dark
        }),
        option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? VARIABLES.palette.background_light : VARIABLES.palette.primary_dark,
            paddingLeft: '1rem',
            cursor: 'pointer',
            backgroundColor: state.isSelected ? VARIABLES.palette.primary_dark : 'inherit',
            '&:hover': {
                color: VARIABLES.palette.secondary_color
            }
        }),
        indicatorsContainer: provided => ({
            ...provided,
            padding: '0 .75rem',
            paddingLeft: '0'
        }),
        indicatorSeparator: provided => ({
            ...provided,
            backgroundColor: 'inherit'
        }),
        menu: provided => ({
            ...provided,
            // backgroundColor: variant === 'primary' ? VARIABLES.palette.background_color : 'inherit',
            backgroundColor: '#fafafa', // BackgroundColor hardcoded on  secondary variant
            borderRadius: '.5rem',
            position: 'absolute',
            zIndex: 3200,
            // bottom: '.5rem',
            border: variant === 'primary' ? 'none' : `1px solid ${VARIABLES.palette.content_light}`,
            boxShadow: '0px 1px 4px 0px #0000002e',
            fontSize: '.875rem',
            overflowY: 'hidden'
        }),
        menuList: provided => ({
            ...provided,
            '&::-webkit-scrollbar': {
                width: '1rem',
                overflowY: 'hidden'
            },
            '&::-webkit-scrollbar-thumb': {
                borderRight: '.5rem solid #0000',
                backgroundClip: 'padding-box',
                backgroundColor: VARIABLES.palette.content_color,
                paddingTop: 5,
                paddingRight: 5,
                maxHeight: 100
            }
        })
    };

    return (
        <Grid
            className={`ui-SelectInput${!label ? ' labelless-input' : ''}${className ? ` ${className}` : ''}`}
            item
            md={md}
            lg={lg}
            sm={sm}
            xs={xs === 1 ? undefined : xs}
            style={{
                width: xs === 1 ? '6rem' : ''
            }}
        >
            {label && (
                <Typography
                    variant="input-head"
                    className="ui-Input-label"
                    color={disabled ? 'content-light' : 'primary-dark'}
                >
                    {`${label || '\xa0' }${optional ? ' (opcional)' : ''}`}
                </Typography>
            )}
            {(value && isClearable) && (
                <IconButton
                    disableRipple
                    className="ui-SelectInput-clear-button"
                    disabled={disabled}
                    onClick={() => {
                        setSelectedOption(null);
                        onChange(null);
                    }}
                >
                    <Close
                        color={disabled ? VARIABLES.palette.content_light : VARIABLES.palette.secondary_color}
                        className="ui-Input-icon right-icon"
                    />
                </IconButton>
            )}
            <Select
                // menuIsOpen // style & debug purposes only
                onInputChange={(value, action: InputActionMeta) => WriteSelect(value, action)}
                value={{
                    label: selectIcon ? (
                        <Grid
                            container
                            alignItems="center"
                        >
                            <Grid
                                item
                                className="ui-SelectInput-value-label-icon"
                            >
                                {selectIcon}
                            </Grid>
                            <Grid
                                item
                            >
                                <Typography
                                    color="primary-dark"
                                >
                                    {selectedOption?.label}
                                </Typography>
                            </Grid>
                        </Grid>
                    ) : selectedOption?.label,
                    value: selectedOption?.value
                }}
                menuPlacement="auto"
                inputValue={inputValue}
                maxMenuHeight={150}
                name={name}
                isDisabled={disabled}
                noOptionsMessage={noOptionsMessage ? noOptionsMessage : () => 'No hay opciones disponibles'}
                placeholder={placeholder}
                options={optionsToShow}
                isOptionDisabled={(option: SelectOption) => option.value === value}
                autoFocus={autoFocus}
                onChange={(option: SelectOption, action: ActionMeta<OptionTypeBase>) => handleSelect(option, action)}
                components={{
                    DropdownIndicator: () => (
                        <>
                            { (!value || !isClearable) && (
                                <ChevronBigDown
                                    color={disabled ? VARIABLES.palette.content_light : VARIABLES.palette.secondary_color}
                                />
                            )}
                        </>
                    ),
                    MenuList
                }}
                styles={styles}
            />
        </Grid>
    );
};

export default SelectInput;
